// Tiered pallet-day pricing — TRS §5.2.
//
// Forward-only billing: each calendar day a pallet is on-site is billed at
// the rate of the tier the day's index falls into. The cheaper rate is
// **not** retroactive.
//
// Worked example (TRS §5.2):
//   Tiers: 1–60 @ 0.20 €/day, 61–180 @ 0.15 €/day, 181+ @ 0.12 €/day
//   70 days on-site → 60 × 0.20 + 10 × 0.15 = 13.50 €
//
// Money is always in integer cents. Day indexing is 1-based (the first
// calendar day on-site is day 1).
import { billingThroughDate } from './pallet-state.js';
/** The pricing-tier kind a given billing mode must use. */
export function pricingKindForBillingMode(mode) {
    return mode === 'FIXED_MONTHLY' ? 'FIXED_MONTHLY' : 'PER_DAY';
}
/** True when a tier of `kind` may be assigned to a customer billed `mode`. */
export function pricingTierMatchesBillingMode(kind, mode) {
    return kind === pricingKindForBillingMode(mode);
}
const MS_PER_DAY = 24 * 60 * 60 * 1000;
/**
 * Compute storage charges for one pallet between arrival and the relevant
 * end date (dispatched / side-state-frozen / today). Pure function.
 */
export function computePalletBilling(input) {
    if (input.fixedMonthly === true)
        return zero();
    if (input.state === 'EXPECTED' || input.state === 'CANCELLED')
        return zero();
    const start = utcMidnight(input.arrivedAt);
    const end = utcMidnight(billingEnd(input));
    if (end < start)
        return zero();
    const tiersSorted = [...input.tiers].sort((a, b) => a.fromDay - b.fromDay);
    const overrides = input.overrides ?? [];
    const perDay = [];
    let totalCents = 0;
    // Sum FLAT_AMOUNT overrides separately — they replace the per-day calc
    // for their range entirely.
    const flatAmountRanges = overrides.filter((o) => o.mode === 'FLAT_AMOUNT');
    let flatAmountTotal = 0;
    for (const o of flatAmountRanges) {
        flatAmountTotal += o.valueCents;
    }
    let dayIndex = 1;
    for (let cursor = start; cursor <= end; cursor = new Date(cursor.getTime() + MS_PER_DAY)) {
        if (isWithinAnyFlatAmountRange(cursor, flatAmountRanges)) {
            // Day rolled into a fixed-amount override; do not double-count.
            dayIndex += 1;
            continue;
        }
        const rate = resolveDayRate(cursor, dayIndex, tiersSorted, overrides);
        perDay.push({ date: cursor, rateCents: rate });
        totalCents += rate;
        dayIndex += 1;
    }
    totalCents += flatAmountTotal;
    return {
        totalCents,
        days: perDay.length,
        perDay,
    };
}
function billingEnd(input) {
    const asOf = input.asOf ?? new Date();
    const lifecycleEnd = billingThroughDate({
        arrivedAt: input.arrivedAt,
        state: input.state,
        dispatchedAt: input.dispatchedAt,
        depletedAt: input.depletedAt,
        sideStateAt: input.sideStateAt,
        today: asOf,
    });
    return lifecycleEnd < asOf ? lifecycleEnd : asOf;
}
function resolveDayRate(date, dayIndex, tiers, overrides) {
    // Per-pallet FLAT_RATE override wins over the tier table.
    for (const o of overrides) {
        if (o.mode !== 'FLAT_RATE')
            continue;
        if (date >= utcMidnight(o.periodFrom) && date <= utcMidnight(o.periodTo)) {
            return o.valueCents;
        }
    }
    for (const tier of tiers) {
        const upper = tier.toDay ?? Number.POSITIVE_INFINITY;
        if (dayIndex >= tier.fromDay && dayIndex <= upper)
            return tier.rateCents;
    }
    return 0;
}
function isWithinAnyFlatAmountRange(date, ranges) {
    for (const r of ranges) {
        if (date >= utcMidnight(r.periodFrom) && date <= utcMidnight(r.periodTo))
            return true;
    }
    return false;
}
function utcMidnight(d) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}
function zero() {
    return { totalCents: 0, days: 0, perDay: [] };
}
// ------------------------------------------------------------------
// Validation — tier sets must be well-formed.
// ------------------------------------------------------------------
export class InvalidPricingTierError extends Error {
    constructor(message) {
        super(message);
        this.name = 'InvalidPricingTierError';
    }
}
export function assertValidTiers(tiers) {
    if (tiers.length === 0)
        throw new InvalidPricingTierError('tiers must not be empty');
    const sorted = [...tiers].sort((a, b) => a.fromDay - b.fromDay);
    if (sorted[0].fromDay !== 1) {
        throw new InvalidPricingTierError('first tier must start at day 1');
    }
    for (let i = 0; i < sorted.length; i += 1) {
        const t = sorted[i];
        if (!Number.isInteger(t.fromDay) || t.fromDay < 1) {
            throw new InvalidPricingTierError(`tier ${i}: fromDay must be a positive integer`);
        }
        if (t.toDay !== null && (!Number.isInteger(t.toDay) || t.toDay < t.fromDay)) {
            throw new InvalidPricingTierError(`tier ${i}: toDay must be ≥ fromDay or null`);
        }
        if (!Number.isInteger(t.rateCents) || t.rateCents < 0) {
            throw new InvalidPricingTierError(`tier ${i}: rateCents must be a non-negative integer`);
        }
        const next = sorted[i + 1];
        if (next) {
            if (t.toDay === null) {
                throw new InvalidPricingTierError(`tier ${i}: only the last tier can be open-ended`);
            }
            if (next.fromDay !== t.toDay + 1) {
                throw new InvalidPricingTierError(`tier ${i}: not contiguous with next tier`);
            }
        }
    }
}
//# sourceMappingURL=pricing.js.map